/**
 * Finds what is inflating an SMS's segment count, and clears it.
 *
 * A message is priced as GSM-7 (160 chars per segment) until a single non-GSM
 * character appears — then the *whole* message becomes UCS-2 and the segment
 * drops to 70 chars. In the rich composer that trip-wire is usually invisible:
 * `htmlToMultilineText` carries editor formatting into the SMS as characters,
 * so bold/italic re-base onto the Unicode math-sans alphabets, underline and
 * strike ride as combining marks, and a bulleted list prefixes each item with
 * `•` — none of which the agent typed, and all of which are non-GSM.
 *
 * So detection runs over the editor HTML (formatting included), not over the
 * plain text, and each fix rewrites that HTML: the composers feed it straight
 * back into the editor.
 */

import {
  isGsmChar,
  isGsmExtendedChar,
  splitGraphemes,
} from '#/shared/lib/sms-segments'

export const SMS_COST_KIND = {
  /** Bold/italic/underline/strike — sent as math-sans + combining characters. */
  formatting: 'formatting',
  /** A `•` marker is prepended to every bullet item (numbered items are safe). */
  bulletList: 'bulletList',
  emoji: 'emoji',
  /** Curly quotes, en/em dashes, ellipsis, nbsp — each has a plain equivalent. */
  smartPunctuation: 'smartPunctuation',
  /** Any other non-GSM character; transliterated to its base letter if it has one. */
  otherUnicode: 'otherUnicode',
  /** GSM-safe, but costs two characters instead of one: `{}[]~^\|€`. */
  gsmExtended: 'gsmExtended',
} as const

export type SmsCostKind = (typeof SMS_COST_KIND)[keyof typeof SMS_COST_KIND]

export interface SmsCostIssue {
  kind: SmsCostKind
  /** Occurrences in the message — characters, or affected characters for marks. */
  count: number
  /** Distinct offending characters, for the chips; empty for formatting/lists. */
  samples: string[]
}

/** Inline marks `htmlToMultilineText` turns into styled Unicode characters. */
const MARK_SELECTOR = 'strong, b, em, i, u, s, del, strike'

/** Non-GSM punctuation an editor (or a paste from Word/Docs) slips in, mapped
 *  to the plain equivalent that reads the same and stays GSM-7. Shared with
 *  `sms-sanitize`, which applies the same folding on the send path. */
export const PLAIN_EQUIVALENT: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '‚': "'",
  '′': "'",
  '“': '"',
  '”': '"',
  '„': '"',
  '″': '"',
  '«': '"',
  '»': '"',
  '–': '-',
  '—': '-',
  '−': '-',
  '•': '-',
  '·': '-',
  '…': '...',
  ' ': ' ',
  ' ': ' ',
  ' ': ' ',
  '™': 'TM',
  '®': '(R)',
  '©': '(C)',
}

const EMOJI = /\p{Extended_Pictographic}/u

function classify(grapheme: string): SmsCostKind | null {
  if (EMOJI.test(grapheme)) return SMS_COST_KIND.emoji
  if (grapheme in PLAIN_EQUIVALENT) return SMS_COST_KIND.smartPunctuation
  if (grapheme.length > 1 || !isGsmChar(grapheme)) {
    return SMS_COST_KIND.otherUnicode
  }
  if (isGsmExtendedChar(grapheme)) return SMS_COST_KIND.gsmExtended
  return null
}

/** Strip diacritics (é → e) and keep the result only when it lands back in
 *  GSM-7; anything with no plain counterpart (emoji, other scripts) is dropped. */
function transliterate(grapheme: string): string {
  const stripped = grapheme.normalize('NFD').replace(/\p{M}/gu, '')
  const kept = splitGraphemes(stripped).every(isGsmChar)
  return kept ? stripped : ''
}

/** What each character-level fix replaces an offending grapheme with. */
function replacementFor(grapheme: string, kind: SmsCostKind): string {
  if (kind === SMS_COST_KIND.smartPunctuation) {
    return PLAIN_EQUIVALENT[grapheme] ?? ''
  }
  if (kind === SMS_COST_KIND.otherUnicode) return transliterate(grapheme)
  return ''
}

function parse(html: string): HTMLElement {
  const div = document.createElement('div')
  div.innerHTML = html
  return div
}

function textNodes(root: HTMLElement): Text[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const nodes: Text[] = []
  let node = walker.nextNode()
  while (node) {
    nodes.push(node as Text)
    node = walker.nextNode()
  }
  return nodes
}

/** Characters that are actually restyled: `styleChar` leaves whitespace alone,
 *  so a bold space costs nothing and must not be reported as an offender. */
function styledCharCount(root: HTMLElement): number {
  return textNodes(root)
    .filter((node) => node.parentElement?.closest(MARK_SELECTOR))
    .reduce((sum, node) => sum + node.data.replace(/\s/g, '').length, 0)
}

function tally(root: HTMLElement): Map<SmsCostKind, string[]> {
  const found = new Map<SmsCostKind, string[]>()
  for (const node of textNodes(root)) {
    for (const grapheme of splitGraphemes(node.data)) {
      const kind = classify(grapheme)
      if (!kind) continue
      found.set(kind, [...(found.get(kind) ?? []), grapheme])
    }
  }
  return found
}

/** Distinct offenders, in first-seen order, so the chips don't repeat a char. */
function distinct(graphemes: string[]): string[] {
  return [...new Set(graphemes)]
}

/**
 * Everything in this message that costs segments, most impactful first —
 * formatting and lists lead because they inflate the message without the agent
 * ever typing a special character. An empty list means the body is clean GSM-7.
 */
export function detectSmsCostIssues(html: string): SmsCostIssue[] {
  if (typeof document === 'undefined' || !html) return []
  const root = parse(html)
  const issues: SmsCostIssue[] = []

  const styled = styledCharCount(root)
  if (styled > 0) {
    issues.push({ kind: SMS_COST_KIND.formatting, count: styled, samples: [] })
  }
  const bullets = root.querySelectorAll('ul li').length
  if (bullets > 0) {
    issues.push({
      kind: SMS_COST_KIND.bulletList,
      count: bullets,
      samples: [],
    })
  }

  const found = tally(root)
  const ORDER: SmsCostKind[] = [
    SMS_COST_KIND.emoji,
    SMS_COST_KIND.otherUnicode,
    SMS_COST_KIND.smartPunctuation,
    SMS_COST_KIND.gsmExtended,
  ]
  for (const kind of ORDER) {
    const graphemes = found.get(kind)
    if (!graphemes) continue
    issues.push({
      kind,
      count: graphemes.length,
      samples: distinct(graphemes),
    })
  }
  return issues
}

/** Drop a mark element but keep its text (and any nested formatting). */
function unwrap(element: Element): void {
  element.replaceWith(...Array.from(element.childNodes))
}

/** Bullets cost UCS-2, numbers don't (`1. ` is plain GSM), so a bulleted list is
 *  renumbered rather than flattened — the agent keeps their list. */
function renumberBullets(root: HTMLElement): void {
  root.querySelectorAll('ul').forEach((list) => {
    const ordered = document.createElement('ol')
    ordered.append(...Array.from(list.childNodes))
    list.replaceWith(ordered)
  })
}

/** Rewrite one text node, dropping the gap a removed character leaves behind:
 *  `Done 🎉 now` becomes `Done now`, not `Done  now`. */
function rewriteText(data: string, kind: SmsCostKind): string {
  const graphemes = splitGraphemes(data)
  const out: string[] = []
  graphemes.forEach((grapheme, index) => {
    if (classify(grapheme) !== kind) {
      out.push(grapheme)
      return
    }
    const replacement = replacementFor(grapheme, kind)
    if (replacement) {
      out.push(replacement)
      return
    }
    const last = index === graphemes.length - 1
    const spaceAfter = last || graphemes[index + 1] === ' '
    if (out[out.length - 1] === ' ' && spaceAfter) out.pop()
  })
  return out.join('')
}

function rewriteChars(root: HTMLElement, kind: SmsCostKind): void {
  for (const node of textNodes(root)) {
    const next = rewriteText(node.data, kind)
    if (next !== node.data) node.data = next
  }
}

/** Clear one kind of cost from the editor HTML, returning the new HTML. */
export function clearSmsCostIssue(html: string, kind: SmsCostKind): string {
  if (typeof document === 'undefined') return html
  const root = parse(html)
  if (kind === SMS_COST_KIND.formatting) {
    root.querySelectorAll(MARK_SELECTOR).forEach(unwrap)
  } else if (kind === SMS_COST_KIND.bulletList) {
    renumberBullets(root)
  } else {
    rewriteChars(root, kind)
  }
  return root.innerHTML
}

/** Clear every detected cost in one go. */
export function clearAllSmsCostIssues(html: string): string {
  return detectSmsCostIssues(html).reduce(
    (current, issue) => clearSmsCostIssue(current, issue.kind),
    html,
  )
}
